(() => {
    const KEY = '__konhillProductThumbnailExactMediaV2';

    if (window[KEY]) return;
    window[KEY] = true;

    const getThumbnail = (event) => {
      const target = event.target;

      if (!(target instanceof Element)) return null;

      const thumbnail = target.closest(
        '.slideshow-controls__thumbnail[data-konhill-media-id]'
      );

      if (!thumbnail || thumbnail.hidden) return null;

      /*
       * Only affect the KONHILL product media gallery.
       * Other theme slideshows keep Horizon's native behavior.
       */
      if (
        !thumbnail.closest(
          'media-gallery[data-konhill-gallery]'
        )
      ) {
        return null;
      }

      return thumbnail;
    };

    const getTargetSlide = (
      slideshow,
      mediaId
    ) => {
      if (!slideshow || !mediaId) return null;

      const slides = Array.from(
        slideshow.querySelectorAll(
          'slideshow-slide[slide-id]'
        )
      );

      return (
        slides.find(
          (slide) =>
            String(
              slide.getAttribute('slide-id') || ''
            ) === String(mediaId)
        ) || null
      );
    };

    const isTargetSelected = (
      slideshow,
      mediaId
    ) => {
      const targetSlide = getTargetSlide(
        slideshow,
        mediaId
      );

      if (!targetSlide) return false;

      if (
        targetSlide.getAttribute(
          'aria-hidden'
        ) === 'false'
      ) {
        return true;
      }

      const slides = Array.from(
        slideshow.querySelectorAll(
          'slideshow-slide:not([hidden]), slideshow-slide[reveal]'
        )
      );

      const targetIndex =
        slides.indexOf(targetSlide);

      return (
        targetIndex >= 0 &&
        Number(slideshow.current) ===
          targetIndex
      );
    };

    const updateSelectedThumbnail = (thumbnail) => {
      if (!thumbnail) return;

      const controls = thumbnail.closest(
        'slideshow-controls'
      );

      if (!controls) return;

      controls
        .querySelectorAll(
          '.slideshow-controls__thumbnail[aria-selected="true"]'
        )
        .forEach((item) => {
          if (item !== thumbnail) {
            item.setAttribute(
              'aria-selected',
              'false'
            );
          }
        });

      thumbnail.setAttribute(
        'aria-selected',
        'true'
      );
    };

    const selectByMediaId = (
      thumbnail
    ) => {
      if (!thumbnail?.isConnected) return;

      const mediaId =
        thumbnail.dataset.konhillMediaId;

      if (!mediaId) return;

      /*
       * Our exact-media-id click handler intentionally blocks Horizon's
       * native numeric click handler. Keep the thumbnail selected state
       * in sync manually so the current thumbnail can show its frame.
       */
      updateSelectedThumbnail(
        thumbnail
      );

      const slideshow = thumbnail.closest(
        'slideshow-component'
      );

      if (!slideshow) return;

      slideshow.setAttribute(
        'actioned',
        ''
      );

      let attempt = 0;
      const maxAttempts = 8;

      const run = () => {
        if (
          !slideshow.isConnected ||
          !getTargetSlide(
            slideshow,
            mediaId
          )
        ) {
          return;
        }

        if (
          typeof slideshow.select ===
            'function'
        ) {
          /*
           * IMPORTANT:
           * Select by slide-id, NOT numeric index.
           * Horizon's numeric select uses its filtered `slides` array,
           * which can temporarily change during product/gallery updates.
           */
          slideshow.select(
            { id: String(mediaId) },
            undefined,
            { animate: false }
          );
        }

        attempt += 1;

        if (
          attempt < maxAttempts &&
          !isTargetSelected(
            slideshow,
            mediaId
          )
        ) {
          window.requestAnimationFrame(run);
        }
      };

      if (
        customElements.get(
          'slideshow-component'
        )
      ) {
        run();
      } else {
        customElements
          .whenDefined(
            'slideshow-component'
          )
          .then(run);
      }
    };

    document.addEventListener(
      'click',
      (event) => {
        const thumbnail =
          getThumbnail(event);

        if (!thumbnail) return;

        /*
         * Stop Horizon's numeric /select/{index} handler for this one
         * thumbnail click. We immediately replace it with an exact
         * media-id selection, so two competing selections cannot occur.
         */
        event.preventDefault();
        event.stopImmediatePropagation();

        selectByMediaId(thumbnail);
      },
      true
    );

    /*
     * Keep thumbnail aria-selected in sync with the slide that Horizon
     * is ACTUALLY showing. This is important after a color/variant change
     * because Horizon may replace the entire <media-gallery>.
     *
     * We match by exact slide-id/media-id, never by numeric position.
     */
    const syncSelectedFromSlideshow = (slideshow) => {
      if (!slideshow?.isConnected) return;

      const gallery = slideshow.closest(
        'media-gallery[data-konhill-gallery]'
      );

      if (!gallery) return;

      const slides = Array.from(
        slideshow.querySelectorAll(
          'slideshow-slide[slide-id]'
        )
      );

      /*
       * During a Horizon slide transition there can be a very short
       * moment where the old slide has already lost aria-hidden="false"
       * but the new slide has not received it yet.
       *
       * IMPORTANT: do NOT fall back to the first visible slide here.
       * That fallback made the selection frame flash back to thumbnail 1
       * for one frame before moving to the real active thumbnail.
       * Keep the current thumbnail state unchanged until Horizon exposes
       * the real active slide.
       */
      const activeSlide =
        slides.find(
          (slide) =>
            !slide.hidden &&
            slide.getAttribute('aria-hidden') === 'false'
        );

      if (!activeSlide) return;

      const mediaId = String(
        activeSlide.getAttribute('slide-id') || ''
      );

      if (!mediaId) return;

      gallery
        .querySelectorAll(
          'slideshow-controls[thumbnails]'
        )
        .forEach((controls) => {
          const thumbnails = Array.from(
            controls.querySelectorAll(
              '.slideshow-controls__thumbnail[data-konhill-media-id]'
            )
          );

          const selectedThumbnail =
            thumbnails.find(
              (thumbnail) =>
                !thumbnail.hidden &&
                String(
                  thumbnail.dataset.konhillMediaId || ''
                ) === mediaId
            );

          if (!selectedThumbnail) return;

          thumbnails.forEach((thumbnail) => {
            const selected = thumbnail === selectedThumbnail ? 'true' : 'false';
            if (thumbnail.getAttribute('aria-selected') !== selected) {
              thumbnail.setAttribute('aria-selected', selected);
            }
          });
        });
    };

    const syncGallerySelection = (gallery) => {
      if (!(gallery instanceof Element)) return;

      gallery
        .querySelectorAll('slideshow-component')
        .forEach(syncSelectedFromSlideshow);
    };

    const gallerySelector = 'media-gallery[data-konhill-gallery]';
    const observedGalleries = new Map();
    const pendingGalleries = new Set();
    let syncFrame = 0;

    const scheduleGallery = (gallery) => {
      pendingGalleries.add(gallery);
      if (syncFrame) return;
      syncFrame = window.requestAnimationFrame(() => {
        syncFrame = 0;
        pendingGalleries.forEach((pending) => {
          if (pending.isConnected) syncGallerySelection(pending);
        });
        pendingGalleries.clear();
      });
    };

    const observeGallery = (gallery) => {
      if (observedGalleries.has(gallery)) return;
      const observer = new MutationObserver((mutations) => {
        if (mutations.some((mutation) =>
          mutation.type === 'childList' ||
          mutation.target.matches?.('slideshow-slide[slide-id]')
        )) scheduleGallery(gallery);
      });
      observer.observe(gallery, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['aria-hidden', 'hidden'],
      });
      observedGalleries.set(gallery, observer);
      syncGallerySelection(gallery);
    };

    const discoverGalleries = (root) => {
      if (!(root instanceof Element)) return;
      if (root.matches(gallerySelector)) observeGallery(root);
      root.querySelectorAll(gallerySelector).forEach(observeGallery);
    };

    const installGalleryObservers = () => {
      discoverGalleries(document.body);
      const discoveryObserver = new MutationObserver((mutations) => {
        const added = new Set();
        let hasRemovals = false;
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element && node.isConnected &&
                !node.parentElement?.closest(gallerySelector)) added.add(node);
          });
          if (mutation.removedNodes.length) hasRemovals = true;
        });
        added.forEach((node) => {
          if (![...added].some((parent) => parent !== node && parent.contains(node))) {
            discoverGalleries(node);
          }
        });
        if (hasRemovals) observedGalleries.forEach((observer, gallery) => {
          if (!gallery.isConnected) {
            observer.disconnect();
            observedGalleries.delete(gallery);
            pendingGalleries.delete(gallery);
          }
        });
      });
      discoveryObserver.observe(document.body, { subtree: true, childList: true });
    };

    document.addEventListener('slideshow:select', (event) => {
      const gallery = event.target.closest?.(gallerySelector);
      if (gallery) scheduleGallery(gallery);
    });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', installGalleryObservers, { once: true });
    } else {
      installGalleryObservers();
    }

  })();

(() => {
      const GLOBAL_KEY = '__konhillColorGalleryLoaderV2';

      if (window[GLOBAL_KEY]) return;
      window[GLOBAL_KEY] = true;

      const states = new WeakMap();

      const normalize = (value) =>
        String(value || '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' ');

      const isColorName = (value) => {
        const name = normalize(value);

        return (
          name === 'color' ||
          name === 'colour' ||
          name === '颜色' ||
          name === '顏色'
        );
      };

      const getOptionName = (control) => {
        if (!(control instanceof Element)) return '';

        const explicit =
          control.getAttribute('data-option-name');

        if (explicit) return explicit;

        const fieldset =
          control.closest('fieldset');

        if (fieldset) {
          const legend =
            fieldset.querySelector(':scope > legend');

          if (legend?.textContent) {
            /*
             * Swatch legends can include the selected value,
             * e.g. "Color Blush Pink".
             * Match the leading option label.
             */
            const legendText =
              legend.textContent.trim();

            const firstWord =
              legendText.split(/\s+/)[0];

            if (isColorName(firstWord)) {
              return firstWord;
            }

            if (isColorName(legendText)) {
              return legendText;
            }
          }
        }

        if (
          control instanceof HTMLSelectElement
        ) {
          const match =
            control.name?.match(
              /^options\[(.*)\]$/
            );

          if (match?.[1]) {
            return match[1];
          }

          const label =
            control.id
              ? document.querySelector(
                  `label[for="${CSS.escape(
                    control.id
                  )}"]`
                )
              : null;

          if (label?.textContent) {
            return label.textContent.trim();
          }
        }

        /*
         * Final fallback for Horizon radio names:
         * "Color-block-id-product-id"
         */
        if (
          control instanceof HTMLInputElement &&
          control.name
        ) {
          const firstPart =
            control.name.split('-')[0];

          if (isColorName(firstPart)) {
            return firstPart;
          }
        }

        return '';
      };

      const isMainProductPicker = (
        picker
      ) => {
        if (!picker) return false;

        if (
          picker.closest(
            'product-card, quick-add-dialog'
          )
        ) {
          return false;
        }

        return true;
      };

      const getScope = (picker) =>
        picker.closest(
          'product-information'
        ) ||
        picker.closest(
          '.shopify-section'
        ) ||
        document;

      const getGallery = (scope) =>
        scope.querySelector(
          'media-gallery[data-konhill-gallery]'
        ) ||
        scope.querySelector(
          'media-gallery'
        );

      const getHost = (gallery) =>
        gallery?.closest(
          '.product-information__media'
        ) ||
        gallery?.parentElement ||
        null;

      const removeExistingState = (
        host
      ) => {
        const state =
          states.get(host);

        if (!state) return;

        state.observer?.disconnect();

        if (state.timer) {
          clearTimeout(
            state.timer
          );
        }

        if (state.imageTimer) {
          clearTimeout(
            state.imageTimer
          );
        }

        state.overlay?.remove();

        states.delete(host);
      };

      const createOverlay = (host) => {
        const overlay =
          document.createElement('div');

        overlay.className =
          'konhill-color-gallery-loader';

        overlay.setAttribute(
          'role',
          'status'
        );

        overlay.setAttribute(
          'aria-label',
          'Loading selected color images'
        );

        overlay.innerHTML =
          '<span class="konhill-color-gallery-loader__spinner" aria-hidden="true"></span>';

        host.appendChild(overlay);

        return overlay;
      };

      const getMainImage = (
        gallery
      ) => {
        if (!gallery) return null;

        return (
          gallery.querySelector(
            'slideshow-slide[aria-hidden="false"] img'
          ) ||
          gallery.querySelector(
            'slideshow-slide img'
          ) ||
          gallery.querySelector(
            '.product-media-container img'
          ) ||
          gallery.querySelector('img')
        );
      };

      const finish = (
        host
      ) => {
        const state =
          states.get(host);

        if (!state) return;

        state.observer?.disconnect();

        if (state.timer) {
          clearTimeout(
            state.timer
          );
        }

        if (state.imageTimer) {
          clearTimeout(
            state.imageTimer
          );
        }

        if (state.overlay) {
          state.overlay.style.opacity =
            '0';

          window.setTimeout(
            () => {
              state.overlay?.remove();
            },
            150
          );
        }

        states.delete(host);
      };

      const waitForNewMainImage = (
        host,
        newGallery
      ) => {
        const state =
          states.get(host);

        if (!state) return;

        const image =
          getMainImage(newGallery);

        /*
         * No image (for example video/model as first media):
         * replacement is already complete, so finish shortly.
         */
        if (!image) {
          window.setTimeout(
            () => finish(host),
            180
          );

          return;
        }

        const completeLoading = () => {
          /*
           * Small minimum delay prevents a 1-frame flash
           * on extremely fast connections.
           */
          window.setTimeout(
            () => finish(host),
            160
          );
        };

        if (
          image.complete &&
          image.naturalWidth > 0
        ) {
          completeLoading();
          return;
        }

        image.addEventListener(
          'load',
          completeLoading,
          {
            once: true,
          }
        );

        image.addEventListener(
          'error',
          completeLoading,
          {
            once: true,
          }
        );

        state.imageTimer =
          window.setTimeout(
            completeLoading,
            5000
          );
      };

      const rememberPendingThumbnail = (
        event
      ) => {
        const target =
          event.target;

        if (!(target instanceof Element)) {
          return;
        }

        const thumbnail =
          target.closest(
            '.slideshow-controls__thumbnail[data-konhill-media-id]'
          );

        if (
          !thumbnail ||
          thumbnail.hidden
        ) {
          return;
        }

        const gallery =
          thumbnail.closest(
            'media-gallery[data-konhill-gallery]'
          );

        if (!gallery) return;

        const host =
          getHost(gallery);

        if (!host) return;

        const state =
          states.get(host);

        /*
         * Only remember a thumbnail click while the OLD gallery is still
         * waiting to be replaced by a color/variant response.
         */
        if (
          !state ||
          state.oldGallery !== gallery
        ) {
          return;
        }

        const mediaId =
          thumbnail.dataset
            .konhillMediaId;

        if (!mediaId) return;

        state.pendingMediaId =
          String(mediaId);
      };

      const replayPendingThumbnail = (
        host,
        newGallery
      ) => {
        const state =
          states.get(host);

        if (!state) return;

        const mediaId =
          state.pendingMediaId;

        if (!mediaId) return;

        let attempt = 0;
        const maxAttempts = 8;

        const selectInNewGallery =
          () => {
            if (
              !newGallery?.isConnected
            ) {
              return;
            }

            const slideshow =
              newGallery.querySelector(
                'slideshow-component'
              );

            if (!slideshow) return;

            const targetSlide =
              Array.from(
                slideshow.querySelectorAll(
                  'slideshow-slide[slide-id]'
                )
              ).find(
                (slide) =>
                  String(
                    slide.getAttribute(
                      'slide-id'
                    ) || ''
                  ) === String(mediaId)
              );

            if (!targetSlide) {
              return;
            }

            if (
              typeof slideshow.select ===
                'function'
            ) {
              /*
               * Exact slide-id selection survives sorted_media reordering
               * and temporary slide filtering during Horizon updates.
               */
              slideshow.select(
                { id: String(mediaId) },
                undefined,
                { animate: false }
              );
            }

            attempt += 1;

            const selected =
              targetSlide.getAttribute(
                'aria-hidden'
              ) === 'false';

            if (
              !selected &&
              attempt < maxAttempts
            ) {
              window.requestAnimationFrame(
                selectInNewGallery
              );
            }
          };

        if (
          customElements.get(
            'slideshow-component'
          )
        ) {
          window.requestAnimationFrame(
            selectInNewGallery
          );
        } else {
          customElements
            .whenDefined(
              'slideshow-component'
            )
            .then(() => {
              window.requestAnimationFrame(
                selectInNewGallery
              );
            });
        }
      };

      const start = (
        control
      ) => {
        const picker =
          control.closest(
            'variant-picker'
          );

        if (
          !isMainProductPicker(
            picker
          )
        ) {
          return;
        }

        const optionName =
          getOptionName(control);

        if (
          !isColorName(
            optionName
          )
        ) {
          return;
        }

        const scope =
          getScope(picker);

        const oldGallery =
          getGallery(scope);

        if (!oldGallery) {
          return;
        }

        const host =
          getHost(oldGallery);

        if (!host) {
          return;
        }

        /*
         * Rapid color switching:
         * clean previous loader/observer first.
         */
        removeExistingState(
          host
        );

        const overlay =
          createOverlay(host);

        const state = {
          oldGallery,
          overlay,
          observer: null,
          timer: null,
          imageTimer: null,
          /*
           * If the shopper clicks a visible thumbnail while the color
           * request is still in flight, remember its exact media ID.
           * After Horizon replaces the gallery, replay that same media ID
           * instead of relying on a numeric slide position.
           */
          pendingMediaId: null,
        };

        states.set(
          host,
          state
        );

        /*
         * Horizon media-gallery.js uses:
         * this.replaceWith(newMediaGallery)
         *
         * Watch for that exact replacement.
         */
        state.observer =
          new MutationObserver(
            () => {
              const currentGallery =
                getGallery(scope);

              if (
                !currentGallery ||
                currentGallery ===
                  oldGallery
              ) {
                return;
              }

              state.observer?.disconnect();

              /*
               * If the shopper clicked a thumbnail while the color request
               * was pending, restore the exact media ID on the new gallery.
               * This avoids index mismatch after sorted_media is rebuilt.
               */
              replayPendingThumbnail(
                host,
                currentGallery
              );

              waitForNewMainImage(
                host,
                currentGallery
              );
            }
          );

        state.observer.observe(
          host,
          {
            childList: true,
            subtree: true,
          }
        );

        /*
         * Hard failsafe:
         * never leave a permanent loader after
         * a failed Shopify variant request.
         */
        state.timer =
          window.setTimeout(
            () => finish(host),
            9000
          );
      };

      /*
       * Capture phase means the spinner is created
       * immediately when the color change fires,
       * before Horizon finishes its async variant work.
       */
      document.addEventListener(
        'click',
        rememberPendingThumbnail,
        true
      );

      document.addEventListener(
        'change',
        (event) => {
          const control =
            event.target;

          if (
            !(
              control instanceof
              Element
            )
          ) {
            return;
          }

          if (
            !control.closest(
              'variant-picker'
            )
          ) {
            return;
          }

          start(control);
        },
        true
      );

    })();
